import React, { useState, useEffect } from "react";
import { Container, Table, Spinner, Button, Form, Row, Col, InputGroup, ButtonGroup, ToggleButton } from "react-bootstrap";
import { FiSearch, FiEye, } from "react-icons/fi";
import { BsFileText } from "react-icons/bs";
import instance from "../../../services/baseService";
import toast from "react-hot-toast";
import Base from '../../Base';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useNavigate, useParams } from "react-router-dom";
import PaginationComponent from "../../common/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { setScripReportFilters, clearScripReportFilters } from "../../../slices/commonReducer";
import { ImDownload2 } from "react-icons/im";
import { exportScripReportToExcel } from "./Scripreportexcelexport";

function ScripReport() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { client_id } = useParams();

    // Get filters from Redux
    const scripReportFilters = useSelector((state) => state.common.scripReportFilters);
    const [loadingDownload, setLoadingDownload] = useState(false);

    const [loadingData, setLoadingData] = useState(false);
    const [scripData, setScripData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const startDateRef = React.useRef(null);
    const endDateRef = React.useRef(null);
    const PER_PAGE = 25;

    // Local state synced with Redux
    const [searchQuery, setSearchQuery] = useState(scripReportFilters.searchQuery);
    const [startDate, setStartDate] = useState(scripReportFilters.startDate);
    const [endDate, setEndDate] = useState(scripReportFilters.endDate);
    const [currentPage, setCurrentPage] = useState(scripReportFilters.currentPage);
    const [manualBrc, setManualBrc] = useState(scripReportFilters.manualBrc);
    const [selectedScrips, setSelectedScrips] = useState([]);
    // Sync local state with Redux on mount
    useEffect(() => {
        setSearchQuery(scripReportFilters.searchQuery);
        setStartDate(scripReportFilters.startDate);
        setEndDate(scripReportFilters.endDate);
        setCurrentPage(scripReportFilters.currentPage);
        setManualBrc(scripReportFilters.manualBrc);
    }, []);

    useEffect(() => {
        fetchScripData();
    }, [client_id, searchQuery, startDate, endDate, currentPage, manualBrc]);

    useEffect(() => {
        setSelectedScrips([]);
    }, [currentPage]);

    // Update Redux whenever filters change
    useEffect(() => {
        dispatch(setScripReportFilters({
            searchQuery,
            startDate,
            endDate,
            currentPage,
            manualBrc
        }));
    }, [searchQuery, startDate, manualBrc, endDate, currentPage, dispatch]);

    const fetchScripData = async () => {
        setLoadingData(true);

        try {
            // Determine client_id based on client_id 
            const apiClientId = client_id ? client_id : "0";

            // Build URL
            const url = `/api/v1/reconciliation/scrip/report/${apiClientId}`;

            // Make POST request with filters
            const response = await instance.post(url, {
                search: searchQuery,
                start_date: startDate,
                end_date: endDate,
                page: currentPage,
                per_page: PER_PAGE,
                manual_brc: manualBrc
            });

            if (response.data && response.data.type === 'success') {
                setScripData(response.data.data || []);
                setTotalCount(response.data.total_count || 0);
            } else {
                throw new Error(response.data?.message || 'Failed to fetch scrip data');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to load scrip data');
            setScripData([]);
            setTotalCount(0);
        } finally {
            setLoadingData(false);
        }
    };

    const handlePagination = (page) => {
        setCurrentPage(page);
    };

    const handleViewPort = (scrip) => {
        navigate(`/port-code-details/${scrip.client_id}/${scrip.period_id}/${scrip.id}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const clearFilters = () => {
        setSearchQuery("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
        setManualBrc(null);
        dispatch(clearScripReportFilters());
    };

    const handleBackToHome = () => {
        navigate(-1);
    };

    const getStatusDisplay = (status) => {
        const statusStyles = {
            11: { text: 'No BRC Updated', color: '#dc3545', bg: '#f8d7da' },
            12: { text: 'BRC Partially Updated', color: '#fd7e14', bg: '#fff3cd' },
            13: { text: 'BRC Updated', color: '#198754', bg: '#d1e7dd' }
        };

        return statusStyles[status] || { text: 'Unknown Status', color: '#6c757d', bg: '#e2e3e5' };
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedScrips(scripData.map(scrip => scrip.id));
        } else {
            setSelectedScrips([]);
        }
    };

    const handleSelectScrip = (scripId) => {
        setSelectedScrips(prev => {
            if (prev.includes(scripId)) {
                return prev.filter(id => id !== scripId);
            } else {
                return [...prev, scripId];
            }
        });
    };

    const getTotalSelectedAmount = () => {
        const total = scripData
            .filter(scrip => selectedScrips.includes(scrip.id))
            .reduce((sum, scrip) => sum + (parseFloat(scrip.scrip_amount) || 0), 0);
        return formatAmount(total);
    };

    const isAllSelected = scripData.length > 0 && selectedScrips.length === scripData.length;

    const handleDownloadExcel = async () => {
        setLoadingDownload(true);
        try {
            const result = await exportScripReportToExcel({
                data: scripData,
                selectedScrips,
                searchQuery,
                startDate,
                endDate,
                manualBrc,
                currentPage,
                perPage: PER_PAGE
            });

            if (result.success) {
                toast.success('Excel downloaded successfully');
            } else {
                toast.error(result.message || 'Failed to download Excel');
            }
        } catch (error) {
            console.error('Excel download error:', error);
            toast.error('Failed to download Excel');
        } finally {
            setLoadingDownload(false);
        }
    };

    return (
        <>
            <Base>
                <Container fluid>
                    <div className="mb-4">
                        <div className="d-flex flex-column align-items-start mb-2">
                            <button
                                variant="link"
                                className="text-primary d-flex align-items-center gap-2 p-0 mb-2 transition-transform duration-200 hover:scale-105"
                                onClick={handleBackToHome}
                                style={{ fontSize: "16px", textDecoration: "none", background: "none", border: "none" }}
                            >
                                <FaArrowLeft size={14} /> Back
                            </button>
                            <h1 className="m-0 text-[28px] font-bold text-gray-800">Scrip Report</h1>
                        </div>
                        <Row className="g-4 bg">
                            <Col md={4}>
                                <Form.Label className="mb-1" style={{ fontSize: '14px', visibility: 'hidden' }}>Search</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FiSearch />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by client name, scrip no, port code..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="mb-1" style={{ fontSize: '14px' }}>Start date</Form.Label>
                                    <Form.Control
                                        ref={startDateRef}
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        onClick={() => startDateRef.current?.showPicker()}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="auto" className="d-flex align-items-end pb-3">
                                <FaArrowRight size={20} className="text-muted" />
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="mb-1" style={{ fontSize: '14px' }}>End date</Form.Label>
                                    <Form.Control
                                        ref={endDateRef}
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        onClick={() => endDateRef.current?.showPicker()}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="mb-1" style={{ fontSize: '14px' }}>BRC Type</Form.Label>
                                    <ButtonGroup className="w-100">
                                        <ToggleButton
                                            id="brc-all"
                                            type="radio"
                                            variant="outline-primary"
                                            name="brc-type"
                                            value={null}
                                            checked={manualBrc === null}
                                            onChange={() => setManualBrc(null)}
                                            style={{ fontSize: '14px' }}
                                        >
                                            All
                                        </ToggleButton>
                                        <ToggleButton
                                            id="brc-manual"
                                            type="radio"
                                            variant="outline-primary"
                                            name="brc-type"
                                            value={1}
                                            checked={manualBrc === 1}
                                            onChange={() => setManualBrc(1)}
                                            style={{ fontSize: '14px' }}
                                        >
                                            Manual
                                        </ToggleButton>
                                        <ToggleButton
                                            id="brc-auto"
                                            type="radio"
                                            variant="outline-primary"
                                            name="brc-type"
                                            value={0}
                                            checked={manualBrc === 0}
                                            onChange={() => setManualBrc(0)}
                                            style={{ fontSize: '14px' }}
                                        >
                                            Auto
                                        </ToggleButton>
                                    </ButtonGroup>
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <Form.Group>
                                    <Form.Label className="mb-1" style={{ fontSize: '14px', visibility: 'hidden' }}>Clear</Form.Label>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        className="w-100"
                                    >
                                        Clear
                                    </Button>
                                </Form.Group>
                            </Col>
                        </Row>
                        {selectedScrips.length > 0 && (
                            <div className="mt-3 p-3 bg-light border rounded">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">
                                        Total Selected: {selectedScrips.length} scrip(s)
                                    </span>
                                    <span className="fw-bold text-primary fs-5">
                                        Total Amount: {getTotalSelectedAmount()}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Table Section */}
                    <div className="overflow-auto position-relative" style={{ maxHeight: '700px', overflowY: 'auto' }}>
                        {loadingData ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-3 text-muted">Loading scrip data...</p>
                            </div>
                        ) : scripData.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="text-muted">No scrip data available</p>
                            </div>
                        ) : (
                            <Table className="core-ui-table seperate-border" hover>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                    <tr>
                                        <th className="text-center align-middle pb-4" style={{ whiteSpace: 'nowrap', minWidth: '50px', width: '50px', maxWidth: '50px', backgroundColor: '#fff' }}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                style={{ display: 'flex', justifyContent: 'center', margin: 0 }}
                                            />
                                        </th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>S.No</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Client Name</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Port Code</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Scrip No.</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Scrip Date</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Scrip Amount</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Type</th>
                                        <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Status</th>
                                        <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scripData.map((scrip, rowIndex) => (
                                        <tr key={scrip.id} >
                                            <td className="text-center align-middle pb-4 " style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '50px', width: '50px', maxWidth: '50px' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedScrips.includes(scrip.id)}
                                                    onChange={() => handleSelectScrip(scrip.id)}
                                                />
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {(currentPage - 1) * PER_PAGE + rowIndex + 1}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {scrip.client_name || 'N/A'}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {scrip.port_code || 'N/A'}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {scrip.scrip_no || 'N/A'}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(scrip.scrip_date)}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {formatAmount(scrip.scrip_amount)}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                {scrip.manual_brc ? "Manual" : "Automation"}
                                            </td>
                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                <span
                                                    style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '4px',
                                                        fontSize: '15px',
                                                        fontWeight: '700',
                                                        color: getStatusDisplay(scrip.status).color,
                                                        backgroundColor: getStatusDisplay(scrip.status).bg
                                                    }}
                                                >
                                                    {getStatusDisplay(scrip.status).text}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleViewPort(scrip)}
                                                    title="View Port"
                                                    className=" me-2 d-inline-flex align-items-center no-hover"
                                                >
                                                    <FiEye className="me-1" />
                                                    View Port
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>

                    {/* Results Count and Pagination */}
                    {!loadingData && scripData.length > 0 && (
                        <div className="mt-3 p-2 bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Showing {((currentPage - 1) * PER_PAGE) + 1} to {Math.min(currentPage * PER_PAGE, totalCount)} of {totalCount} scrip(s)
                                </small>
                                {totalCount > PER_PAGE && (
                                    <div className="d-flex justify-content-center mt-2">
                                        <PaginationComponent
                                            total_count={totalCount}
                                            current_page={currentPage}
                                            per_page={PER_PAGE}
                                            handleOnClick={handlePagination}
                                        />
                                    </div>
                                )}
                                <Button
                                    variant="primary"
                                    className="fw-bold shadow-sm d-flex align-items-center gap-1"
                                    disabled={loadingDownload || scripData.length === 0}
                                    onClick={handleDownloadExcel}
                                >
                                    {loadingDownload ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <ImDownload2 />
                                    )}
                                    Download Excel {selectedScrips.length > 0 && `(${selectedScrips.length})`}
                                </Button>
                            </div>
                        </div>
                    )}
                </Container>
            </Base>
        </>
    );
}

export default ScripReport;